import { json } from 'aurelia-fetch-client';
import { autoinject } from 'aurelia-framework';
import { ApiService } from '../../utils/api-service';
import { IText, translate } from '../../utils/translator';
import { NodeModel } from "../../models/node-model";

export interface IAccountAM {
	id: string;
	accountNumber: string;
	name: string;
	accountGroup: IAccountGroupAM;
	tags: IText[];
}

export interface IAccountGroupAM {
	id: string;
	nameText: IText;
	accountClass: string;
	sortOrder: number;
}

export interface IAccountingDimensionValueAM {
	id: string;
	identifier: string;
	name: string;
	dimension: IAccountingDimensionAM;
}

export interface IAccountingDimensionAM {
	id: string;
	name: string;
}

export interface IAccountListItemGroupAM {
	id: string;
	nameText: string;
	code: string;
}

export interface IAccountListItemTagAM {
	id: string;
	nameText: string;
	code: string;
}

export interface IAccountListItemAM {
	id: string;
	accountNumber: string;
	name: string;
	accountGroup: IAccountListItemGroupAM;
	accountTags: IAccountListItemTagAM[];
	removable: boolean;
	excludeFromCalculation: boolean;
}

export class UpdateAccountAM {
	accountNumber: string;
	name: string;

	constructor(init?: Partial<UpdateAccountAM>) {
		Object.assign(this, init);
	}
}

export class AccountListItemVM {
	id: string;
	accountNumber: string;
	name: string;
	accountGroup: IAccountListItemGroupAM;
	accountTags: IAccountListItemTagAM[];
	accountNumberValid = true;
	accountNumberEditing = false;
	nameValid = true;
	nameEditing = false;
	removable = true;
	excludeFromCalculation: boolean;

	constructor(init?: Partial<AccountListItemVM>) {
		Object.assign(this, init);
	}
}

export interface IAccountGroupStructureAM {
	id: string;
	nameText: string;
	sortOrder: number;
	code: string;
	accountClass: string;
	accounts: IAccountGroupStructureAccountAM[];
}

export interface IAccountGroupStructureAccountAM {
	id: string;
	accountNumber: string;
	name: string;
}

export class AccountGroupStructure extends NodeModel {
	accountClass: string;

	constructor(title: string, sortOrder: number = 1, parentName: string = null, id: string = null) {
		super(title, sortOrder, parentName, id);
		this.nodeType = 'AccountGroup';
	}

	static accountStructureFromAM(item: IAccountGroupStructureAM) {
		const model = new AccountGroupStructure(item.nameText, item.sortOrder, null, item.id);
		model.accountClass = item.accountClass;
		model.children.push(...item.accounts.map(account => AccountStructure.accountFromAM(account, model)));
		return model;
	}
}

export class AccountStructure extends AccountGroupStructure {
	constructor(title: string, sortOrder: number = 1, parentName: string = null, id: string = null) {
		super(title, sortOrder, parentName, id);
		this.nodeType = 'Account';
	}

	static accountFromAM(item: IAccountGroupStructureAccountAM, parent: AccountGroupStructure) {
		const accountNode = new AccountStructure(`${item.accountNumber} - ${item.name}`, null, null, item.id);
		accountNode.parent = parent;
		return accountNode;
	}
}

export enum AccountClass{
	Revenue = 1,
	Expenses = 2
}

@autoinject
export class AccountListService {
	private accountListPromise: Promise<IAccountListItemAM[]>;

	constructor(private http: ApiService) {
	}

	loadAccountList(accountChartId: string): Promise<IAccountListItemAM[]> {
		if (!this.accountListPromise) {
			this.accountListPromise = this.http.fetchJson(`account-charts/${accountChartId}`);
		}

		return this.accountListPromise;
	}

	async loadAccountGroups(customerId: string): Promise<IAccountGroupStructureAM[]> {
		return await this.http.fetchJson(`account-groups/for-customer/${customerId}`);
	}

	updateAccount(account: IAccountListItemAM) {
		return this.http.fetch(
			`accounts/${account.id}`, {
				method: 'put',
				body: json(new UpdateAccountAM({
					accountNumber: account.accountNumber,
					name: account.name
				}))
			}
		);
	}

	removeAccount(accountId: string) {
		return this.http.fetch(`accounts/remove/${accountId}`, {
			method: 'post'
		});
	}

	setExcludeFromCalculation(accountId: string, value: boolean) {
		return this.http.fetch(`accounts/${accountId}/exclude-from-calculation/${value}`, { method: 'put' });
	}

	async filterAccountNumbersByAccountClass(scopeId: string, accounts: string[], accountClass: AccountClass) : Promise<string[]>{

		var result = await this.http.fetchJson(
			`accounts/for-scope/${scopeId}/filter-by-account-class/${AccountClass[accountClass]}`, {
				method: 'post',
				body: json(accounts)
			}
		);

		return result;
	}
}
